// packages/utils/src/shared/i18n/localeClientMeta.ts
//
// The three per-locale facts the embed's widgets read (a language's English name, its flag, the language payment
// pages get), in a table of their own, so a widget bundle does not carry the whole `LOCALE_META` (./localeMeta.ts):
// its other twelve fields serve servers, translation and the quality gates. `LOCALE_META`'s rows spread these, so
// it stays the one complete table, and a locale missing here is a compile error in both. Fill each field with a
// real decision (docs/i18n-adding-a-locale.md §1.1); never copy the `en` row.
import type { AppLocale } from '@tourtask/types/i18n';

export interface LocaleClientMeta {
  /**
   * The language's name in English, as `Intl.DisplayNames(['en'])` gives it; the AI text generators
   * (captions, tour texts) name their target language with it. Translation uses `translationPromptName`.
   */
  readonly englishName: string;
  /**
   * The flag that stands for the language, on web and native, as an ISO 3166 code: a country (alpha-2, 'SE'),
   * or a subdivision (ISO 3166-2, 'ES-CT') for a language whose flag is a region's — Catalan's is Catalonia's
   * Senyera, not Spain's. Web draws the flag-icons file of that name; native draws a country's emoji and the
   * subdivisions in `@tourtask/mobile-ui`'s `flagDrawings.ts`.
   */
  readonly flagCountry: string;
  /** The language sent to payment pages that take the raw locale (PayPal, Adyen, PlaceToPay, Arpix, Payoneer). */
  readonly paymentLanguage: string;
}

export const LOCALE_CLIENT_META: Readonly<Record<AppLocale, LocaleClientMeta>> = {
  en: { englishName: 'English', flagCountry: 'US', paymentLanguage: 'en' },
  es: { englishName: 'Spanish', flagCountry: 'ES', paymentLanguage: 'es' },
  pt: { englishName: 'Portuguese', flagCountry: 'PT', paymentLanguage: 'pt' },
  fr: { englishName: 'French', flagCountry: 'FR', paymentLanguage: 'fr' },
  de: { englishName: 'German', flagCountry: 'DE', paymentLanguage: 'de' },
  it: { englishName: 'Italian', flagCountry: 'IT', paymentLanguage: 'it' },
  ar: { englishName: 'Arabic', flagCountry: 'SA', paymentLanguage: 'ar' },
  ja: { englishName: 'Japanese', flagCountry: 'JP', paymentLanguage: 'ja' },
  zh: { englishName: 'Chinese', flagCountry: 'CN', paymentLanguage: 'zh' },
  nl: { englishName: 'Dutch', flagCountry: 'NL', paymentLanguage: 'nl' },
  ru: { englishName: 'Russian', flagCountry: 'RU', paymentLanguage: 'ru' },
  sv: { englishName: 'Swedish', flagCountry: 'SE', paymentLanguage: 'sv' },
  // A language code is not a country code: 'CA' is Canada, never Catalan's flag. No payment provider offers
  // Catalan, and its readers read Spanish.
  ca: { englishName: 'Catalan', flagCountry: 'ES-CT', paymentLanguage: 'es' },
};
